import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Select, SelectOption, Tooltip, Link } from '@appcues/sonar';
import { Label, FontIcon } from 'ext/components/ui';
import {
  fetchTactics,
  selectTacticGroups,
  selectTacticsStatus,
  NONE_TACTIC_VALUE,
} from 'ext/entities/tactics';

const LEARN_MORE_URL = 'https://app.appcues.com/campaigns';

/**
 * Optional tactic selector for the flow creation modal. Lists tactics grouped
 * by campaign (campaign names render as disabled headers), plus a "None"
 * option. The AGENT_STUDIO flag gate lives at the call site, so this only
 * mounts (and only fetches) when the field should be shown.
 * See CampaignAndTacticApis.md.
 *
 * Renders just the label + select so it slots into the modal's existing
 * FieldSet alongside the other fields. Mobile builder has no shadow DOM, so
 * the Select/Tooltip portal to document.body without a `container`.
 */
export const TacticSelect = ({ value, onChange }) => {
  const dispatch = useDispatch();
  const groups = useSelector(selectTacticGroups);
  const status = useSelector(selectTacticsStatus);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchTactics());
    }
  }, [status, dispatch]);

  const handleValueChange = next => {
    onChange(next === NONE_TACTIC_VALUE ? undefined : next);
  };

  return (
    <>
      <Label htmlFor="tactic">
        Tactic (optional)
        <Tooltip
          useChildrenAsTrigger
          content={
            <span>
              Organize your experiences into campaigns and tactics to unlock ROI
              analytics against your key outcomes.{' '}
              <Link
                href={LEARN_MORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </Link>
            </span>
          }
        >
          <FontIcon icon="question-circle" />
        </Tooltip>
      </Label>
      <Select
        id="tactic"
        placeholder="None"
        width="full-width"
        value={value || NONE_TACTIC_VALUE}
        onValueChange={handleValueChange}
      >
        <SelectOption value={NONE_TACTIC_VALUE} label="None" />
        {groups.map(group => [
          <SelectOption
            key={`header-${group.campaignId}`}
            value={`header-${group.campaignId}`}
            label={group.campaignName}
            disabled
          />,
          ...group.tactics.map(tactic => (
            <SelectOption
              key={tactic.id}
              value={tactic.id}
              label={tactic.name}
            />
          )),
        ])}
      </Select>
    </>
  );
};

TacticSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default TacticSelect;
